import { useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import {
  IcDeleteAdminIcon,
  IcOwnerIcon,
  IcSDeleteIcon,
  IcUserAdminIcon,
  IcUserBanIcon,
} from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IPopoverMenuItem, MenuItemStyle } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { showAlert } from 'APP/Tasks/app/app';
import { GroupType, GroupUserRole } from 'APP/model/group/groupModel.types';
import { Features } from 'APP/packages/features/Features';

import { KNOWN_ERROR } from './ParticipantListItem.constants';
import type {
  IParticipantListItemPresenter,
  IParticipantListItemPresenterParams,
} from './ParticipantListItem.types';

export const useParticipantListItemPresenter = ({
  group,
  participant,
  onReloadParticipantsList,
  onParticipantRemove,
  onUpdateParticipantRole,
  onParticipantClick,
}: IParticipantListItemPresenterParams): IParticipantListItemPresenter => {
  const { t } = useTranslation();

  const presenter = useLocalStore<IParticipantListItemPresenter>(() => ({
    group,
    participant,
    menuReference: null,
    isMenuLoading: false,

    get isOwner(): boolean {
      return presenter.participant.role === GroupUserRole.Owner;
    },

    get isAdmin(): boolean {
      return presenter.participant.role === GroupUserRole.Admin;
    },

    get isMember(): boolean {
      return presenter.participant.role === GroupUserRole.Member;
    },

    get isBot(): boolean {
      return presenter.participant.user.isBot;
    },

    get canTransferOwnership(): boolean {
      return !!(presenter.group?.isOwner && !presenter.isBot && !presenter.participant.user.isMe);
    },

    get canAddToAdmins(): boolean {
      return !!(presenter.group?.isOwner && presenter.isMember);
    },

    get canRemoveFromAdmins(): boolean {
      return !!(presenter.group?.isOwner && presenter.isAdmin);
    },

    get canBanMember(): boolean {
      if (!Features.isBanUserEnabled()) {
        return false;
      }

      if (presenter.participant.user.isMe) {
        return false;
      }

      return !!presenter.group?.isOwner || !!(presenter.group?.isAdmin && presenter.isMember);
    },

    get canRemoveMember(): boolean {
      if (presenter.participant.user.isMe) {
        return false;
      }

      return !!presenter.group?.isOwner || !!(presenter.group?.isAdmin && presenter.isMember);
    },

    get isOpenedMenu(): boolean {
      return !!presenter.menuReference;
    },

    get menuItems(): IPopoverMenuItem[] {
      return [
        {
          key: 'transferOwnership',
          isVisible: presenter.canTransferOwnership,
          title: t('common_transfer_ownership'),
          onClick: presenter.transferOwnership,
          icon: IcOwnerIcon,
        },
        {
          key: 'add',
          isVisible: presenter.canAddToAdmins,
          title: t('add_to_admins'),
          onClick: presenter.addToAdmins,
          icon: IcUserAdminIcon,
        },
        {
          key: 'removeAdmin',
          isVisible: presenter.canRemoveFromAdmins,
          title: t('remove_from_admins'),
          onClick: presenter.removeFromAdmins,
          icon: IcDeleteAdminIcon,
        },
        {
          key: 'ban',
          isVisible: presenter.canBanMember,
          title: t('ban_user'),
          onClick: presenter.banParticipant,
          icon: IcUserBanIcon,
        },
        {
          key: 'delete',
          isVisible: presenter.canRemoveMember,
          title: t('delete_user'),
          onClick: presenter.removeParticipant,
          icon: IcSDeleteIcon,
          style: MenuItemStyle.Danger,
        },
      ].filter((x) => x.isVisible);
    },

    get hint(): string {
      if (presenter.group?.isMember && presenter.group?.isOpen) {
        return '';
      }

      if (presenter.isOwner) {
        return t('common_owner_label');
      }
      if (presenter.isAdmin) {
        return t('common_admin_label');
      }
      return '';
    },

    onToggleMenu(menuReference: HTMLElement): void {
      if (presenter.isOpenedMenu) {
        presenter.onCloseMenu();
        return;
      }
      presenter.menuReference = menuReference;
    },

    onCloseMenu(): void {
      this.menuReference = null;
    },

    showErrorMessage(error: string): void {
      showAlert(t(KNOWN_ERROR[error] || 'common_somethings_wrong_error'));
    },

    onClick(): void {
      onParticipantClick(presenter.participant);
    },

    async transferOwnership(): Promise<void> {
      const canTransfer = await Tasks.app.showConfirm({
        text: t(
          presenter.group?.verified
            ? 'badge_transfer_ownership_alert'
            : 'common_transfer_ownership_alert',
          { 0: participant.user.displayName }
        ),
      });

      if (!canTransfer) {
        return;
      }
      const userId = presenter.participant.user.id;

      presenter.isMenuLoading = true;

      const { error } = await Tasks.group.transferOwnership(presenter.group!.id, userId);

      presenter.isMenuLoading = false;

      if (error) {
        presenter.showErrorMessage(error);
        return;
      }

      onReloadParticipantsList();
    },

    async addToAdmins(): Promise<void> {
      const canAddToAdmins = await Tasks.app.showConfirm({
        text: t('confirmation_text_add_admin', { 0: presenter.participant.user.displayName }),
      });

      if (!canAddToAdmins) {
        return;
      }

      const userId = presenter.participant.user.id;

      presenter.isMenuLoading = true;

      const { error } = await Tasks.group.addGroupAdmins(presenter.group!.id, [userId]);

      presenter.isMenuLoading = false;

      if (error) {
        presenter.showErrorMessage(error);
        return;
      }

      onUpdateParticipantRole(userId, GroupUserRole.Admin);
    },

    async removeFromAdmins(): Promise<void> {
      const canRemoveFromAdmins = await Tasks.app.showConfirm({
        text: t('confirmation_text_remove_admin', { 0: presenter.participant.user.displayName }),
      });

      if (!canRemoveFromAdmins) {
        return;
      }

      const userId = presenter.participant.user.id;

      presenter.isMenuLoading = true;

      const { error } = await Tasks.group.removeGroupAdmins(presenter.group!.id, [userId]);

      presenter.isMenuLoading = false;

      if (error) {
        presenter.showErrorMessage(error);
        return;
      }

      onUpdateParticipantRole(userId, GroupUserRole.Member);
    },

    async banParticipant(): Promise<void> {
      const { isConfirmed: canRemoveFromAdmins, isChecked: deleteAllMessages } =
        await Tasks.app.showConfirmWithCheckbox({
          text: t('ban_user_alert', { 0: participant.user.displayName }),
          checkBoxText: t('checkbox_delete_messages'),
        });

      if (!canRemoveFromAdmins) {
        return;
      }
      const userId = presenter.participant.user.id;

      presenter.isMenuLoading = true;

      const { error } = await Tasks.group.banParticipants(
        presenter.group!.id,
        [userId],
        deleteAllMessages
      );

      presenter.isMenuLoading = false;

      if (error) {
        presenter.showErrorMessage(error);
        return;
      }

      onParticipantRemove(userId);

      deleteAllMessages
        ? Tasks.app.showToast(t('delete_banned_user_messages_snackbar'))
        : Tasks.app.showToast(
            t('common_user_banned_snackbar', { 0: participant.user.displayName })
          );
    },

    async removeParticipant(): Promise<void> {
      const canRemove = await Tasks.app.showConfirm({
        text: presenter.group!.isChannel
          ? t('delete_user_from_channel_alert')
          : t('delete_user_from_group_alert'),
      });

      if (!canRemove) {
        return;
      }

      const userId = presenter.participant.user.id;

      presenter.isMenuLoading = true;

      const { error } =
        presenter.group?.type === GroupType.SecretGroup
          ? await Tasks.e2ee.removeMembersFromSecretGroup(presenter.group!.id, [userId])
          : await Tasks.group.removeParticipants(presenter.group!.id, [userId]);

      presenter.isMenuLoading = false;

      if (error) {
        presenter.showErrorMessage(error);
        return;
      }

      onParticipantRemove(userId);
    },
  }));

  useEffect(() => {
    presenter.participant = participant;
    presenter.group = group;
  }, [group, participant]);

  return presenter;
};
